import { useTranslations } from 'next-intl';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClipboardFiles } from '@/app/hooks';
import { cn } from '@/app/utils';

type FileDropZoneBaseProps = {
  onFilesDrop: (files?: FileList | File[]) => void;
  dropPlaceholder?: string;
  enablePaste?: boolean;
  showPlaceholder?: boolean;
  disabled?: boolean;
};

export type FileDropZoneProps = FileDropZoneBaseProps & {
  /**
   * fullscreen 模式下拖拽文件进入浏览器窗口时，自动展示全屏蒙层作为拖放目标。
   * 关闭后仅当前容器响应拖拽和粘贴。
   */
  fullscreen?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop' | 'onDragOver' | 'onDragEnter' | 'onDragLeave'>;

interface FullscreenFileDropZoneSubscriber {
  element: () => HTMLDivElement | null;
  onFilesDrop: (files?: FileList | File[]) => void;
  setVisible: (visible: boolean) => void;
}

// fullscreen 模式监听的是 document 级拖拽事件。
// 多个实例同时存在时，必须共享同一套全局监听和拖拽计数，
// 否则各自独立计数会在 dragleave 时互相“误伤”，导致蒙层提前消失。
const fullscreenFileDropZoneSubscribers = new Set<FullscreenFileDropZoneSubscriber>();
let fullscreenDragCounter = 0;
let hasBoundFullscreenListeners = false;

function hasDraggedFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

function syncFullscreenDropZoneVisibility(visible: boolean): void {
  fullscreenFileDropZoneSubscribers.forEach((subscriber) => {
    subscriber.setVisible(visible);
  });
}

function resetFullscreenDropZoneState(): void {
  fullscreenDragCounter = 0;
  syncFullscreenDropZoneVisibility(false);
}

function resolveFullscreenDropTargets(target: EventTarget | null): FullscreenFileDropZoneSubscriber[] {
  const subscribers = Array.from(fullscreenFileDropZoneSubscribers);
  if (!(target instanceof Node)) {
    return subscribers.length === 1 ? subscribers : [];
  }

  // drop 时优先把文件路由给真实落点所在的 zone。
  // 这样可以避免多个 fullscreen zone 同时收到一次 drop，触发重复上传。
  const matchedSubscribers = subscribers.filter((subscriber) => {
    const element = subscriber.element();
    return element?.contains(target) ?? false;
  });

  if (matchedSubscribers.length > 0) {
    return matchedSubscribers;
  }

  return subscribers.length === 1 ? subscribers : [];
}

function handleFullscreenDragEnter(event: DragEvent): void {
  if (!hasDraggedFiles(event) || fullscreenFileDropZoneSubscribers.size === 0) return;

  fullscreenDragCounter += 1;
  syncFullscreenDropZoneVisibility(true);
}

function handleFullscreenDragLeave(event: DragEvent): void {
  if (!hasDraggedFiles(event) || fullscreenFileDropZoneSubscribers.size === 0) return;

  fullscreenDragCounter -= 1;
  if (fullscreenDragCounter <= 0) {
    resetFullscreenDropZoneState();
  }
}

function handleFullscreenDragOver(event: DragEvent): void {
  if (!hasDraggedFiles(event) || fullscreenFileDropZoneSubscribers.size === 0) return;

  event.preventDefault();
}

function handleFullscreenDrop(event: DragEvent): void {
  if (!hasDraggedFiles(event) || fullscreenFileDropZoneSubscribers.size === 0) return;

  event.preventDefault();
  const dropTargets = resolveFullscreenDropTargets(event.target);
  const files = event.dataTransfer?.files;

  resetFullscreenDropZoneState();
  dropTargets.forEach((subscriber) => {
    subscriber.onFilesDrop(files);
  });
}

function bindFullscreenListeners(): void {
  if (hasBoundFullscreenListeners) return;

  document.addEventListener('dragenter', handleFullscreenDragEnter);
  document.addEventListener('dragleave', handleFullscreenDragLeave);
  document.addEventListener('dragover', handleFullscreenDragOver);
  document.addEventListener('drop', handleFullscreenDrop);
  hasBoundFullscreenListeners = true;
}

function unbindFullscreenListeners(): void {
  if (!hasBoundFullscreenListeners) return;

  document.removeEventListener('dragenter', handleFullscreenDragEnter);
  document.removeEventListener('dragleave', handleFullscreenDragLeave);
  document.removeEventListener('dragover', handleFullscreenDragOver);
  document.removeEventListener('drop', handleFullscreenDrop);
  hasBoundFullscreenListeners = false;
}

function subscribeFullscreenFileDropZone(subscriber: FullscreenFileDropZoneSubscriber): () => void {
  fullscreenFileDropZoneSubscribers.add(subscriber);
  bindFullscreenListeners();

  return () => {
    subscriber.setVisible(false);
    fullscreenFileDropZoneSubscribers.delete(subscriber);

    if (fullscreenFileDropZoneSubscribers.size === 0) {
      resetFullscreenDropZoneState();
      unbindFullscreenListeners();
    }
  };
}

export const FileDropZone = forwardRef<HTMLDivElement, FileDropZoneProps>(
  (
    {
      onFilesDrop,
      children,
      dropPlaceholder,
      enablePaste = false,
      className,
      showPlaceholder = true,
      fullscreen,
      disabled,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations();
    const [visible, setVisible] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        elementRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
          return;
        }

        if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    useClipboardFiles({
      targetRef: elementRef,
      enabled: enablePaste && !disabled,
      onPasteFiles: onFilesDrop,
    });

    useEffect(() => {
      if (!fullscreen) return;

      if (disabled) {
        setVisible(false);
        return;
      }

      // fullscreen 模式下组件本身不直接绑定 document 事件，
      // 只注册到共享控制器，由共享控制器统一维护可见性和 drop 分发。
      return subscribeFullscreenFileDropZone({
        element: () => elementRef.current,
        onFilesDrop,
        setVisible,
      });
    }, [fullscreen, disabled, onFilesDrop]);

    if (fullscreen) {
      return (
        <>
          <div ref={setRefs} className={className} {...(props as React.HTMLAttributes<HTMLDivElement>)}>
            {children}
          </div>
          {showPlaceholder &&
            visible &&
            createPortal(
              <div className="pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center bg-system-black64">
                <span className="text-[#508DFF] text-sm">{dropPlaceholder ?? t('create.dragUpload.placeholder')}</span>
              </div>,
              document.body,
            )}
        </>
      );
    }

    return (
      <div
        ref={setRefs}
        className={cn('relative', className)}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          setVisible(true);
        }}
        onDragEnter={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          setVisible(false);
        }}
        onDrop={(e) => {
          if (disabled) return;
          setVisible(false);
          e.preventDefault();
          e.stopPropagation();
          const files = e.dataTransfer.files;
          onFilesDrop(files);
        }}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      >
        {children}
        {showPlaceholder && visible && (
          <div
            className={cn(
              className,
              'pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-16 border border-[#508DFF] bg-system-black56 backdrop-blur-3xl',
            )}
          >
            <span className="text-[#508DFF] text-sm">{dropPlaceholder ?? t('create.dragUpload.placeholder')}</span>
          </div>
        )}
      </div>
    );
  },
);

FileDropZone.displayName = 'FileDropZone';
